import { Search } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router";

import { AccountMenu } from "@/features/acceso/AccountMenu";
import { useUsuario } from "@/features/acceso/api";
import { NotificationBell } from "@/features/avisos/NotificationBell";
import { BuscadorDeAcciones } from "@/features/buscador/BuscadorDeAcciones";
import { ReportarMejora } from "@/features/mejoras/GrabarMejora";

import { UndoBar } from "../UndoBar";


/**
 * **La barra superior blanca con el membrete de NECA** (#30, en todo el ERP): dónde se está, el
 * buscador de acciones (Ctrl+K), «Pendientes» con su número, «Reportar mejora» (M13; menú de cuatro desde 0182), deshacer y la cuenta.
 *
 * **No repite el menú lateral**: las secciones siguen a la izquierda, en su árbol. La barra lleva lo que
 * el árbol no tiene —llegar escribiendo y lo que falta por hacer— y el logo, que salió de la barra
 * lateral para no estar dos veces. Por dentro es un panel claro: los tonos claros de la cuenta y del
 * deshacer se leen aquí en tinta, sin tocar esas piezas.
 */
export function BarraSuperior({ menuMovil, ubicacion }: { menuMovil: ReactNode; ubicacion: ReactNode }) {
  const [buscando, setBuscando] = useState(false);
  const usuario = useUsuario();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== "k") return;
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;
      setBuscando((abierto) => !abierto);
    };
    const abrir = () => setBuscando(true);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("neca:abrir-buscador", abrir);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("neca:abrir-buscador", abrir);
    };
  }, []);

  return (
    <header className="panel-light flex flex-wrap items-center gap-3 border-b border-line px-4 py-2 shadow-none [--color-light-2:var(--color-ink-2)] [--color-light-3:var(--color-ink-3)] [--color-light:var(--color-ink)] [--color-white:var(--color-ink)] lg:flex-nowrap lg:px-7">
      {menuMovil}
      <Link to="/" className="flex shrink-0 items-center gap-3 rounded-md py-1">
        <img src="/logo.svg" alt="NECA · Inicio" className="h-5 w-auto" />
        <span className="hidden text-[10px] leading-tight font-semibold tracking-[0.18em] text-ink-3 uppercase 2xl:block">
          Prefabricados decorativos
          <br />
          en concreto
        </span>
      </Link>
      <span aria-hidden className="hidden h-6 w-px bg-line lg:block" />
      <div className="hidden min-w-0 flex-1 overflow-hidden whitespace-nowrap lg:block">{ubicacion}</div>

      <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2 lg:shrink-0 lg:flex-nowrap">
        <button
          type="button"
          onClick={() => setBuscando(true)}
          aria-label="Buscar o ir a"
          aria-keyshortcuts="Control+K"
          className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 text-sm text-ink-2 transition-colors hover:text-ink lg:w-60"
        >
          <Search aria-hidden className="size-4 shrink-0" />
          <span aria-hidden className="hidden flex-1 text-left sm:block">Buscar o ir a…</span>
          <kbd aria-hidden className="hidden rounded border border-line bg-surface px-1.5 font-mono text-[10px] text-ink-3 sm:block">Ctrl K</kbd>
        </button>
        <NotificationBell enabled={Boolean(usuario.data)} />
        <ReportarMejora />
        {usuario.data?.practice_url && (
          <a href={usuario.data.practice_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center rounded-lg border border-line px-3 text-sm font-semibold text-ink hover:bg-surface-2">
            Practicar
          </a>
        )}
        <div className="hidden lg:block">
          <UndoBar />
        </div>
        <AccountMenu />
      </div>
      <BuscadorDeAcciones abierto={buscando} onAbiertoChange={setBuscando} />
    </header>
  );
}
